import type { AITTheme } from '@this/shared/ui/theme';

export const getStyles = (theme: AITTheme) => ({
  toggleButton: {
    position: 'fixed' as const,
    bottom: '16px',
    left: '16px',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: theme.accentColor || '#3b82f6',
    color: 'white',
    fontSize: '20px',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    zIndex: 1200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  panel: {
    position: 'fixed' as const,
    bottom: '70px',
    left: '16px',
    width: '500px',
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: '400px',
    backgroundColor: theme.bgColor || '#ffffff',
    border: `1px solid ${theme.grayBorderColor || '#e5e7eb'}`,
    borderRadius: '8px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
    zIndex: 1199,
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.grayBorderColor || '#e5e7eb'}`,
    backgroundColor: theme.bgColorLight || '#f9fafb'
  },
  title: {
    fontWeight: 600,
    fontSize: '14px'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: theme.textColor || '#374151',
    padding: '0 4px'
  },
  toolbar: {
    display: 'flex',
    gap: '8px',
    padding: '8px 16px',
    borderBottom: `1px solid ${theme.grayBorderColor || '#e5e7eb'}`,
    backgroundColor: theme.bgColorLight || '#f9fafb'
  },
  filterSelect: {
    padding: '4px 8px',
    borderRadius: '4px',
    border: `1px solid ${theme.grayBorderColor || '#e5e7eb'}`,
    fontSize: '12px',
    backgroundColor: 'white',
    color: theme.textColor || '#374151'
  },
  toolbarButton: {
    padding: '4px 12px',
    borderRadius: '4px',
    border: `1px solid ${theme.grayBorderColor || '#e5e7eb'}`,
    backgroundColor: 'white',
    color: theme.textColor || '#374151',
    fontSize: '12px',
    cursor: 'pointer'
  },
  logList: {
    flex: 1,
    overflow: 'auto',
    padding: '8px'
  },
  emptyMessage: {
    textAlign: 'center' as const,
    color: theme.textColorLight || '#9ca3af',
    padding: '24px',
    fontSize: '13px'
  },
  logEntry: {
    marginBottom: '4px',
    padding: '8px',
    backgroundColor: theme.bgColorLight || '#f9fafb',
    borderRadius: '4px',
    fontSize: '12px'
  },
  logHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  logHeaderButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    textAlign: 'left' as const,
    font: 'inherit'
  },
  logTime: {
    color: theme.textColorLight || '#9ca3af',
    fontFamily: 'monospace',
    fontSize: '11px'
  },
  logCategory: {
    padding: '2px 6px',
    borderRadius: '4px',
    color: 'white',
    fontSize: '10px',
    fontWeight: 500
  },
  logTitle: {
    flex: 1,
    fontFamily: 'monospace',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const
  },
  expandIcon: {
    fontSize: '10px',
    color: theme.textColorLight || '#9ca3af'
  },
  copyEntryButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    padding: '2px'
  },
  logSummary: {
    marginTop: '4px',
    marginLeft: '80px',
    color: theme.textColorLight || '#6b7280',
    fontSize: '11px'
  },
  logDetails: {
    marginTop: '8px',
    padding: '8px',
    backgroundColor: '#1e293b',
    color: '#e2e8f0',
    borderRadius: '4px',
    fontSize: '11px',
    fontFamily: 'monospace',
    overflow: 'auto',
    maxHeight: '200px',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-all' as const
  },
  tabBar: {
    display: 'flex',
    borderBottom: `1px solid ${theme.grayBorderColor || '#e5e7eb'}`,
    backgroundColor: theme.bgColorLight || '#f9fafb'
  },
  tabButton: {
    padding: '8px 16px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
    color: theme.textColorLight || '#6b7280',
    borderBottom: '2px solid transparent'
  },
  tabButtonActive: {
    color: theme.accentColor || '#3b82f6',
    borderBottomColor: theme.accentColor || '#3b82f6'
  },
  infoContent: {
    flex: 1,
    overflow: 'auto',
    padding: '12px'
  },
  infoSection: {
    marginBottom: '16px'
  },
  infoSectionTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: theme.textColor || '#374151',
    marginBottom: '8px',
    paddingBottom: '4px',
    borderBottom: `1px solid ${theme.grayBorderColor || '#e5e7eb'}`
  },
  infoRow: {
    display: 'flex',
    fontSize: '11px',
    marginBottom: '4px'
  },
  infoLabel: {
    width: '100px',
    color: theme.textColorLight || '#6b7280',
    flexShrink: 0
  },
  infoValue: {
    color: theme.textColor || '#374151',
    fontFamily: 'monospace',
    wordBreak: 'break-all' as const
  },
  reportButton: {
    padding: '4px 12px',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: '#ef4444',
    color: 'white',
    fontSize: '12px',
    cursor: 'pointer',
    fontWeight: 500
  },
  reportModalContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px'
  },
  reportDescription: {
    fontSize: '14px',
    color: theme.textColor || '#374151',
    margin: 0
  },
  searchContent: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column' as const
  },
  searchStateContent: {
    padding: '12px',
    overflow: 'auto'
  },
  tagList: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '6px'
  },
  tag: {
    padding: '4px 8px',
    borderRadius: '4px',
    backgroundColor: '#3b82f6',
    color: 'white',
    fontSize: '11px',
    fontWeight: 500
  },
  missingTag: {
    padding: '4px 8px',
    borderRadius: '4px',
    backgroundColor: '#f59e0b',
    color: 'white',
    fontSize: '11px',
    fontWeight: 500
  },
  emptyText: {
    color: theme.textColorLight || '#9ca3af',
    fontSize: '12px'
  },
  successText: {
    color: '#10b981',
    fontSize: '12px',
    fontWeight: 500
  },
  errorMessage: {
    textAlign: 'center' as const,
    color: '#ef4444',
    padding: '24px',
    fontSize: '13px'
  },
  errorList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px'
  },
  errorItem: {
    padding: '6px 8px',
    backgroundColor: '#fef2f2',
    borderRadius: '4px',
    color: '#ef4444',
    fontSize: '11px',
    border: '1px solid #fecaca'
  },
  jsonBlock: {
    margin: 0,
    padding: '8px',
    backgroundColor: '#1e293b',
    color: '#e2e8f0',
    borderRadius: '4px',
    fontSize: '11px',
    fontFamily: 'monospace',
    overflow: 'auto',
    maxHeight: '200px',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-all' as const
  },
  itemCardList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px'
  },
  itemCard: {
    backgroundColor: theme.bgColorLight || '#f9fafb',
    borderRadius: '6px',
    borderLeft: '4px solid #3b82f6',
    padding: '8px 10px',
    fontSize: '11px'
  },
  itemCardHeader: {
    marginBottom: '6px'
  },
  itemTypeTag: {
    padding: '2px 8px',
    borderRadius: '4px',
    color: 'white',
    fontSize: '10px',
    fontWeight: 600
  },
  itemCardBody: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '3px'
  },
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  itemIcon: {
    fontSize: '10px',
    width: '14px',
    textAlign: 'center' as const
  },
  itemLabel: {
    color: theme.textColorLight || '#6b7280',
    minWidth: '70px',
    flexShrink: 0
  },
  itemValue: {
    color: theme.textColor || '#374151',
    fontFamily: 'monospace',
    fontWeight: 500
  }
});
